/* eslint-disable import/order,import/first */
// these imports supposedly have side-effects so we should import them first
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import CssBaseline from '@mui/material/CssBaseline';

import * as React from 'react';
import * as R from 'ramda';
import {
  createTheme,
  darken,
  lighten,
  getLuminance,
  alpha,
  PaletteOptions,
  Breakpoint,
  ThemeOptions,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import {CacheProvider} from '@emotion/react';
import createCache, {EmotionCache} from '@emotion/cache';
import {isClient} from '../../utils/environment';

function createExtraScopePlugin(...extra: any[]) {
  const scopes = extra.map(scope => `${scope.trim()} `);

  return (element: any) => {
    if (element.type !== 'rule') {
      return;
    }

    if (element.root?.type === '@keyframes') {
      return;
    }

    element.props = element.props
      .map((prop: string) => scopes.map(scope => scope + prop))
      .reduce(
        (scopesArray: string[], scope: string[]) => scopesArray.concat(scope),
        [],
      );
  };
}

const typography = {
  fontFamily: ['Source Sans Pro', 'sans-serif'].join(','),
  fontSize: 15.22,
};

const buttonStateAdjustmentCoefficients = {
  hover: 0.08,
  focus: 0.12,
  active: 0.12,
};

const spacingScalingFactor = 8;

export const palette = {
  primary: {
    main: '#137194',
    50: '#E1F4F6',
    100: '#B3E2EA',
    200: '#85CFDD',
    300: '#5DBCD1',
    400: '#42AECB',
    500: '#2AA1C5',
    600: '#2194B8',
    700: '#1482A7',
    800: '#137194',
    900: '#075273',
  },
  secondary: {
    main: '#E19516',
    50: '#FCFAE5',
    100: '#F8F2BF',
    200: '#F3E994',
    300: '#EEE06B',
    400: '#EBDB4B',
    500: '#E8D52B',
    600: '#E6C325',
    700: '#E4AC1E',
    800: '#E19516',
    900: '#DB6D06',
  },
  error: {
    main: '#B00020',
  },
  warning: {
    main: '#FACC0F',
  },
  success: {
    main: '#087F23',
  },
  text: {
    secondary: 'rgba(0,0,0,0.60)',
  },
  action: {
    hoverOpacity: buttonStateAdjustmentCoefficients.hover,
    focusOpacity: buttonStateAdjustmentCoefficients.focus,
  },
};

const autofillShadow = '0 0 0px 1000px hsl(0, 0%, 96%) inset';
export const defaultFocusStyle = {
  outline: '-webkit-focus-ring-color auto 1px',
};
const theme = createTheme();

export const commonPopoverPositioning = {
  bottomLeft: {
    anchorOrigin: {
      vertical: 'bottom' as const,
      horizontal: 'left' as const,
    },
    transformOrigin: {
      vertical: 'top' as const,
      horizontal: 'left' as const,
    },
  },
};

export const defaultThemeOptions: ThemeOptions = {
  spacing: spacingScalingFactor,
  palette: palette as PaletteOptions,
  typography,
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '1px',
          '& .MuiIconButton-root:not([class*=MuiAutocomplete])': {
            padding: 4,
          },
        },
        input: {
          // make the autofilled input field background unchanged
          '&:-webkit-autofill': {
            WebkitBoxShadow: autofillShadow,
            boxShadow: autofillShadow,
          },
        },
        adornedStart: {
          '& .MuiInputAdornment-positionStart > button': {
            marginLeft: '-11px',
          },
        },
        adornedEnd: {
          '& .MuiInputAdornment-positionEnd > button': {
            marginRight: '-11px',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          maxHeight: '232px',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(2px)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          padding: `${spacingScalingFactor}px ${2 * spacingScalingFactor}px`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          root: {
            '& .MuiBackdrop-root': {
              backdropFilter: 'blur(2px)',
            },
          },
        },
        paperFullScreen: {
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          '& .MuiAppBar-root': {
            position: 'relative',
            height: 'unset',
            borderBottom: 'initial',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          marginBottom: 'env(safe-area-inset-bottom)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': defaultFocusStyle,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {boxShadow: 'none'},
          '&:focus': {boxShadow: 'none'},
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': defaultFocusStyle,
        },
      },
    },
    MuiCardActionArea: {defaultProps: {disableRipple: true}},
    MuiFormControl: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
        margin: 'dense',
        size: 'small',
      },
    },
    MuiButtonBase: {
      defaultProps: {disableRipple: true}, // No more ripple, on the whole application 💣!
      styleOverrides: {
        root: {
          ...typography,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {color: 'primary'},
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            backgroundColor: alpha(
              palette.primary.main,
              palette.action.focusOpacity,
            ),
          },
        },
      },
    },
    MuiMenu: {
      defaultProps: {...commonPopoverPositioning.bottomLeft},
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backdropFilter: 'none',
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
        MenuProps: {
          ...commonPopoverPositioning.bottomLeft,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.keyCode === 32) {
              (e.target as HTMLLIElement).click();
            }
          },
        },
      },
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'none',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {variant: 'outlined'},
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          [theme.breakpoints.up('md')]: {
            minWidth: '160px',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backdropFilter: 'none',
          },
        },
      },
    },
  },
};

/**
 * Use for getting the default theme options but overriding the initial breakpoint (for testing)
 */
export const defaultThemeOptionsAtBreakpoint = (breakpoint: Breakpoint) =>
  R.mergeDeepRight(defaultThemeOptions, {
    props: {
      MuiWithWidth: {
        initialWidth: breakpoint,
      },
    },
  });

const defaultTheme = createTheme(defaultThemeOptions);

type Props = {
  theme?: typeof defaultTheme;
};

// `createCache` is not defined on the server, so we must gatekeep this call to prevent
//   the next server from crashing during module resolution
const cache = isClient
  ? createCache({
      key: 'css',
      stylisPlugins:
        process.env.NODE_ENV === 'test'
          ? []
          : [createExtraScopePlugin(':not(#\\20)')],
    })
  : ({} as EmotionCache);

/**
 * @deprecated This is only intended to be used in legacy contexts with Kohana involved. Prefer CleanThemeProvider or, for theme overrides use MUI's ThemeProvider.
 */
export const ThemeProvider: React.FCC<Props> = ({
  children,
  theme = defaultTheme,
}) => {
  return (
    <ScopedCssBaseline sx={typography}>
      <CacheProvider value={cache}>
        <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
      </CacheProvider>
    </ScopedCssBaseline>
  );
};

/**
 * This is just like ThemeProvider, but it's meant to be used
 * where legacy CSS is not a concern (Next.js)
 */
export const CleanThemeProvider: React.FCC<Props> = ({
  children,
  theme = defaultTheme,
}) => {
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <>{children}</>
    </MuiThemeProvider>
  );
};

/** Given a backgroundColor and adjustment mode,
 *   get the standard style defs for common button states */
export const getButtonBackgroundColorStyles = (
  backgroundColor: string,
  /** Whether the button should darken or lighten on interaction.
   * By default this is derived by measuring the luminance of backgroundColor */
  adjustMode: 'light' | 'dark' = getLuminance(backgroundColor) > 0.5
    ? 'dark'
    : 'light',
) => {
  const adjustFn = adjustMode === 'light' ? lighten : darken;

  return {
    backgroundColor,
    '&:hover, &:focus': {
      backgroundColor: adjustFn(
        backgroundColor,
        buttonStateAdjustmentCoefficients.hover,
      ),
    },
    '&:active': {
      backgroundColor: adjustFn(
        backgroundColor,
        buttonStateAdjustmentCoefficients.active,
      ),
    },
    transition: 'background 0.25s cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  };
};
